import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  KeyIcon,
  ServerIcon,
  CheckCircleIcon,
  ClockIcon,
  CubeIcon,
  TableCellsIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
  ClipboardDocumentIcon
} from '@heroicons/react/24/outline';
import { connectionsAPI } from '../../api/enhancedApiService';
import { useAuth } from '../../contexts/AuthContext';
import BigQueryConnectionForm from '../Connections/components/BigQueryConnectionForm';

const OnboardingConnectionPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState('database-selection'); // database-selection, auth-method, setup, test, discovery
  const [connectionData, setConnectionData] = useState({
    databaseType: 'snowflake',
    authMethod: 'keypair',
    connection_name: '',
    // Snowflake-specific
    account: '',
    username: '',
    private_key: '',
    warehouse: 'COMPUTE_WH',
    database: 'SPARVI',
    schema: 'SANDBOX',
    // BigQuery-specific
    project_id: '',
    dataset: '',
    location: 'US',
    service_account_json: ''
  });
  const [copiedCommand, setCopiedCommand] = useState(false);
  const [keyPair, setKeyPair] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [discoveryResult, setDiscoveryResult] = useState(null);
  const [selectedSchemas, setSelectedSchemas] = useState(new Set());
  const [discoveredTables, setDiscoveredTables] = useState([]);
  // Track the connection id created during this onboarding session so
  // re-testing (e.g., after the back button when discovery looks stuck)
  // reuses the existing connection instead of creating a duplicate.
  // Backend logs from 2026-05-11 showed two POST /api/connections within
  // 25s producing two 'Apple - BigQuery' rows for one signup. Seed from
  // localStorage so a page reload during onboarding also dedupes.
  const [createdConnectionId, setCreatedConnectionId] = useState(
    () => localStorage.getItem('onboardingConnectionId') || null
  );

  // Pre-fill connection name from the organization name captured at signup.
  // Falls back to localStorage if user metadata isn't loaded yet (older
  // sessions may still have the legacy onboardingData blob).
  useEffect(() => {
    const orgFromUser = user?.user_metadata?.organization_name;
    const legacyData = JSON.parse(localStorage.getItem('onboardingData') || '{}');
    const orgName = orgFromUser || legacyData.companyName;
    if (orgName) {
      setConnectionData(prev => ({
        ...prev,
        connection_name: prev.connection_name || `${orgName} - Snowflake`
      }));
    }
  }, [user]);

  // When the user picks a database, update the default connection-name suffix.
  const handleDatabaseSelect = (databaseType) => {
    const orgName =
      user?.user_metadata?.organization_name ||
      JSON.parse(localStorage.getItem('onboardingData') || '{}').companyName;
    const suffix = databaseType === 'bigquery' ? 'BigQuery' : 'Snowflake';
    setConnectionData(prev => ({
      ...prev,
      databaseType,
      connection_name: orgName ? `${orgName} - ${suffix}` : prev.connection_name
    }));
  };

  // BigQuery uses a different auth model (service account JSON) than
  // Snowflake's keypair, so it skips the auth-method step entirely and
  // jumps straight into setup. Snowflake goes through auth-method to let
  // the user (eventually) pick keypair vs password.
  const handleContinueFromDatabaseSelection = () => {
    if (connectionData.databaseType === 'bigquery') {
      setCurrentStep('setup');
      return;
    }
    setCurrentStep('auth-method');
  };

  const generateKeyPair = async () => {
    try {
      setLoading(true);
      const response = await connectionsAPI.generateKeyPair();
      if (response.private_key && response.public_key) {
        setKeyPair(response);
        setConnectionData(prev => ({
          ...prev,
          private_key: response.private_key
        }));
      }
    } catch (error) {
      console.error('Failed to generate key pair:', error);
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    try {
      setLoading(true);
      setTestResult(null);

      let isSuccess = false;
      let message = '';

      if (connectionData.databaseType === 'bigquery') {
        // The generic /connections/test endpoint returns 200 with
        // {message, details} on success (no `success` field) and a 4xx
        // with {error} on failure. Axios throws on 4xx/5xx, so reaching
        // this line means the request succeeded — treat as success.
        const response = await connectionsAPI.test({
          name: connectionData.connection_name || 'BigQuery Connection',
          connection_type: 'bigquery',
          connection_details: {
            project_id: connectionData.project_id,
            dataset: connectionData.dataset || undefined,
            location: connectionData.location || 'US',
            service_account_json: connectionData.service_account_json,
          },
        });
        // Defensive: if the backend ever adds an explicit error field, honor it.
        isSuccess = !response?.error;
        message = response?.message || response?.error || 'Connection successful!';
      } else {
        // Snowflake's testKeyPairConnection uses the legacy {success, message} shape.
        const response = await connectionsAPI.testKeyPairConnection({
          account: connectionData.account,
          username: connectionData.username,
          private_key: connectionData.private_key,
          warehouse: connectionData.warehouse,
          database: connectionData.database,
          schema: connectionData.schema,
        });
        isSuccess = Boolean(response?.success);
        message = response?.message || (isSuccess ? 'Connection successful!' : 'Connection test failed');
      }

      setTestResult({ success: isSuccess, message });

      if (isSuccess) {
        setTimeout(() => {
          setCurrentStep('discovery');
          performSchemaDiscovery();
        }, 1500);
      }
    } catch (error) {
      // Backend returns {error: "..."} on 4xx — axios wraps that in error.response.data.
      const backendMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Connection test failed';
      setTestResult({ success: false, message: backendMessage });
    } finally {
      setLoading(false);
    }
  };

  const performSchemaDiscovery = async () => {
    try {
      setLoading(true);

      let connectionId = createdConnectionId;
      if (!connectionId) {
        if (connectionData.databaseType === 'bigquery') {
          const response = await connectionsAPI.create({
            name: connectionData.connection_name || 'BigQuery Connection',
            connection_type: 'bigquery',
            connection_details: {
              project_id: connectionData.project_id,
              dataset: connectionData.dataset || undefined,
              location: connectionData.location || 'US',
              service_account_json: connectionData.service_account_json,
            },
          });
          connectionId = response?.connection?.id || response?.connection_id;
        } else {
          const response = await connectionsAPI.createKeyPairConnection(connectionData);
          if (!response.success) {
            throw new Error(response.message || 'Failed to create connection');
          }
          connectionId = response.connection_id;
        }

        if (!connectionId) {
          throw new Error('Connection created but no ID returned');
        }

        // Persist immediately so a re-test (or page reload) on this same
        // form reuses the row instead of POSTing a second time.
        setCreatedConnectionId(connectionId);
        localStorage.setItem('onboardingConnectionId', connectionId);
      }

      // Get onboarding data which includes schema discovery (same endpoint for both warehouses).
      const onboardingData = await connectionsAPI.getOnboardingData(connectionId);
      setDiscoveryResult(onboardingData);

      // Auto-select important schemas. Heuristic names favor Snowflake
      // conventions; BigQuery dataset names are typically lowercase so the
      // intersection is small but harmless when empty.
      const importantSchemas = new Set([
        'SPARVI', 'ANALYTICS', 'PRODUCTION',
        'sparvi', 'analytics', 'production',
      ]);
      setSelectedSchemas(importantSchemas);

      if (onboardingData.tables) {
        setDiscoveredTables(onboardingData.tables);
      }
    } catch (error) {
      console.error('Schema discovery failed:', error);
      setTestResult({ success: false, message: `Discovery failed: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const handleContinueToTableSelection = () => {
    // Save discovery results
    localStorage.setItem('discoveryResult', JSON.stringify(discoveryResult));
    localStorage.setItem('selectedSchemas', JSON.stringify(Array.from(selectedSchemas)));
    navigate('/onboarding/tables');
  };

  const publicKeyForSnowflake = keyPair ? 
    keyPair.public_key
      .replace(/-----BEGIN PUBLIC KEY-----/g, '')
      .replace(/-----END PUBLIC KEY-----/g, '')
      .replace(/\n/g, '') 
    : '';

  const snowflakeCommand = connectionData.username && publicKeyForSnowflake
    ? `ALTER USER ${connectionData.username.toUpperCase()} SET RSA_PUBLIC_KEY='${publicKeyForSnowflake}';`
    : '';

  const copySnowflakeCommand = () => {
    if (snowflakeCommand) {
      navigator.clipboard.writeText(snowflakeCommand).then(() => {
        setCopiedCommand(true);
        setTimeout(() => setCopiedCommand(false), 2000);
      });
    }
  };

  const getStepProgress = () => {
    const steps = ['database-selection', 'auth-method', 'setup', 'test', 'discovery'];
    return ((steps.indexOf(currentStep) + 1) / steps.length) * 50; // 50% of total onboarding
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full bg-white rounded-lg shadow-xl p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <ServerIcon className="h-12 w-12 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Smart Connection Wizard
          </h1>
          <p className="text-lg text-gray-600">
            Let's connect to your data warehouse and discover your data
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
            <span>Step 2 of 6</span>
            <span>8 minutes remaining</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-blue-600 h-2 rounded-full transition-all duration-500" style={{ width: `${getStepProgress()}%` }}></div>
          </div>
        </div>

        {/* Database Selection Step */}
        {currentStep === 'database-selection' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Which data warehouse do you use?
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Snowflake */}
              <button
                type="button"
                onClick={() => handleDatabaseSelect('snowflake')}
                className={`rounded-lg p-6 text-center transition-all ${
                  connectionData.databaseType === 'snowflake'
                    ? 'border-2 border-blue-500 bg-blue-50'
                    : 'border border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                }`}
              >
                <img
                  src="/logos/snowflake.svg"
                  alt="Snowflake"
                  className="h-12 w-12 mx-auto mb-3"
                  style={{ filter: 'invert(59%) sepia(67%) saturate(2030%) hue-rotate(167deg) brightness(95%) contrast(91%)' }}
                />
                <h3 className="text-lg font-semibold text-gray-900">Snowflake</h3>
                {connectionData.databaseType === 'snowflake' && (
                  <p className="text-sm font-medium text-blue-600">Selected</p>
                )}
              </button>

              {/* BigQuery */}
              <button
                type="button"
                onClick={() => handleDatabaseSelect('bigquery')}
                className={`rounded-lg p-6 text-center transition-all ${
                  connectionData.databaseType === 'bigquery'
                    ? 'border-2 border-blue-500 bg-blue-50'
                    : 'border border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                }`}
              >
                <img
                  src="/logos/bigquery.svg"
                  alt="Google BigQuery"
                  className="h-12 w-12 mx-auto mb-3"
                  style={{ filter: 'invert(36%) sepia(95%) saturate(1037%) hue-rotate(202deg) brightness(95%) contrast(95%)' }}
                />
                <h3 className="text-lg font-semibold text-gray-900">BigQuery</h3>
                {connectionData.databaseType === 'bigquery' && (
                  <p className="text-sm font-medium text-blue-600">Selected</p>
                )}
              </button>

              {/* Redshift (not yet supported) */}
              <div className="border border-gray-300 rounded-lg p-6 text-center opacity-50">
                <CubeIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-900">Redshift</h3>
                <p className="text-sm text-gray-500">Coming Soon</p>
              </div>
            </div>

            <p className="text-sm text-gray-600 text-center">
              Don&apos;t see yours? We&apos;re adding new databases every month.
            </p>

            <div className="flex justify-end">
              <button
                onClick={handleContinueFromDatabaseSelection}
                className="px-6 py-3 bg-blue-600 text-white font-medium rounded-md shadow-sm hover:bg-blue-700"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Auth Method Selection Step */}
        {currentStep === 'auth-method' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              How would you like to connect to Snowflake?
            </h2>
            
            <div className="space-y-4">
              <div className="border-2 border-blue-500 bg-blue-50 rounded-lg p-6 cursor-pointer">
                <div className="flex items-center">
                  <KeyIcon className="h-8 w-8 text-blue-600 mr-4" />
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">Key Pair Authentication (Recommended)</h3>
                    <p className="text-sm text-gray-600">Most secure, works with SSO</p>
                    <p className="text-sm text-blue-600 font-medium">Setup time: ~3 minutes</p>
                  </div>
                  <CheckCircleIcon className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              
              <div className="border border-gray-300 rounded-lg p-6 opacity-50">
                <div className="flex items-center">
                  <KeyIcon className="h-8 w-8 text-gray-400 mr-4" />
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">Username &amp; Password</h3>
                    <p className="text-sm text-gray-600">Quick setup for testing</p>
                    <p className="text-sm text-gray-500">Setup time: ~1 minute</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <button
                onClick={() => setCurrentStep('database-selection')}
                className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-md shadow-sm hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={() => setCurrentStep('setup')}
                className="px-6 py-3 bg-blue-600 text-white font-medium rounded-md shadow-sm hover:bg-blue-700"
              >
                Use Key Pair Authentication
              </button>
            </div>
          </div>
        )}

        {/* Setup Step — Snowflake */}
        {currentStep === 'setup' && connectionData.databaseType === 'snowflake' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Setup Snowflake Connection
            </h2>

            {/* Step 1: Basic Connection Info */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Step 1: Enter Your Snowflake Details
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Connection Name
                  </label>
                  <input
                    type="text"
                    value={connectionData.connection_name}
                    onChange={(e) => setConnectionData(prev => ({ ...prev, connection_name: e.target.value }))}
                    placeholder="My Snowflake Connection"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Account Identifier
                  </label>
                  <input
                    type="text"
                    value={connectionData.account}
                    onChange={(e) => setConnectionData(prev => ({ ...prev, account: e.target.value }))}
                    placeholder="xy12345.us-east-1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Snowflake Username <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={connectionData.username}
                    onChange={(e) => setConnectionData(prev => ({ ...prev, username: e.target.value }))}
                    placeholder="SPARVI_SERVICE_ACCOUNT"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Enter your Snowflake username. We'll use this to generate the exact command you need.
                  </p>
                </div>
              </div>
            </div>

            {/* Step 2: Generate Key Pair */}
            {!keyPair ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Step 2: Generate Security Keys
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {connectionData.username 
                    ? `Generate a secure key pair for user: ${connectionData.username.toUpperCase()}`
                    : 'Enter your username above to continue'
                  }
                </p>
                <button
                  onClick={generateKeyPair}
                  disabled={loading || !connectionData.username}
                  className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-md shadow-sm hover:bg-blue-700 disabled:opacity-50"
                >
                  <KeyIcon className="h-5 w-5 mr-2" />
                  {loading ? 'Generating...' : 'Generate Key Pair'}
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-green-900 mb-2 flex items-center">
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    Key pair generated successfully
                  </h3>
                  <p className="text-sm text-green-700">
                    Your private key has been copied to the form below.
                  </p>
                </div>

                {/* Snowflake Instructions */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    Step 3: Configure Snowflake
                  </h3>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                    <li>Login to Snowflake as ACCOUNTADMIN</li>
                    <li>Run this command:</li>
                  </ol>
                  <div className="relative mt-3">
                    <div className="p-3 bg-gray-800 text-green-400 rounded-md font-mono text-sm overflow-x-auto pr-12">
                      {snowflakeCommand || 'Enter username above to generate command'}
                    </div>
                    {snowflakeCommand && (
                      <button
                        type="button"
                        onClick={copySnowflakeCommand}
                        className="absolute top-2 right-2 p-2 text-gray-400 hover:text-white transition-colors"
                        title="Copy command"
                      >
                        {copiedCommand ? (
                          <CheckCircleIcon className="h-5 w-5 text-green-400" />
                        ) : (
                          <ClipboardDocumentIcon className="h-5 w-5" />
                        )}
                      </button>
                    )}
                  </div>
                  {copiedCommand && (
                    <p className="text-xs text-green-600 mt-2">Command copied to clipboard!</p>
                  )}
                  <p className="text-xs text-gray-600 mt-2">
                    This command sets the public key for user: <strong>{connectionData.username.toUpperCase()}</strong>
                  </p>
                </div>

                {/* Optional Connection Settings */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Step 4: Optional Settings
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Warehouse
                      </label>
                      <input
                        type="text"
                        value={connectionData.warehouse}
                        onChange={(e) => setConnectionData(prev => ({ ...prev, warehouse: e.target.value }))}
                        placeholder="COMPUTE_WH"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Database
                      </label>
                      <input
                        type="text"
                        value={connectionData.database}
                        onChange={(e) => setConnectionData(prev => ({ ...prev, database: e.target.value }))}
                        placeholder="SPARVI"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-between">
                  <button
                    onClick={() => setCurrentStep('auth-method')}
                    className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-md shadow-sm hover:bg-gray-50"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => setCurrentStep('test')}
                    disabled={!connectionData.connection_name || !connectionData.account || !connectionData.username}
                    className="px-6 py-3 bg-blue-600 text-white font-medium rounded-md shadow-sm hover:bg-blue-700 disabled:opacity-50"
                  >
                    Continue to Test
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Setup Step — BigQuery */}
        {currentStep === 'setup' && connectionData.databaseType === 'bigquery' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Setup BigQuery Connection
            </h2>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Connection Name
              </h3>
              <input
                type="text"
                value={connectionData.connection_name}
                onChange={(e) => setConnectionData(prev => ({ ...prev, connection_name: e.target.value }))}
                placeholder="My BigQuery Connection"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <BigQueryConnectionForm
                details={{
                  service_account_json: connectionData.service_account_json,
                  project_id: connectionData.project_id,
                  dataset: connectionData.dataset,
                  location: connectionData.location,
                }}
                onChange={(updated) => setConnectionData(prev => ({
                  ...prev,
                  service_account_json: updated.service_account_json ?? prev.service_account_json,
                  project_id: updated.project_id ?? prev.project_id,
                  dataset: updated.dataset ?? prev.dataset,
                  location: updated.location ?? prev.location,
                }))}
              />
            </div>

            <div className="flex justify-between">
              <button
                onClick={() => setCurrentStep('database-selection')}
                className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-md shadow-sm hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={() => setCurrentStep('test')}
                disabled={
                  !connectionData.connection_name ||
                  !connectionData.service_account_json ||
                  !connectionData.project_id
                }
                className="px-6 py-3 bg-blue-600 text-white font-medium rounded-md shadow-sm hover:bg-blue-700 disabled:opacity-50"
              >
                Continue to Test
              </button>
            </div>
          </div>
        )}

        {/* Test Step */}
        {currentStep === 'test' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Test Connection
            </h2>

            <div className="text-center py-8">
              {!testResult ? (
                <button
                  onClick={testConnection}
                  disabled={loading}
                  className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-md shadow-sm hover:bg-blue-700 disabled:opacity-50"
                >
                  <ServerIcon className="h-5 w-5 mr-2" />
                  {loading ? 'Testing...' : 'Test Connection'}
                </button>
              ) : (
                <div className={`p-6 rounded-lg ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <CheckCircleIcon className={`h-12 w-12 mx-auto mb-4 ${testResult.success ? 'text-green-500' : 'text-red-500'}`} />
                  <h3 className={`text-lg font-medium ${testResult.success ? 'text-green-900' : 'text-red-900'}`}>
                    {testResult.success ? 'Connection successful' : 'Connection failed'}
                  </h3>
                  <p className={`text-sm mt-2 ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
                    {testResult.message}
                  </p>
                  {testResult.success && (
                    <p className="text-sm text-green-600 mt-2">
                      Now scanning your database for tables and schemas...
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Before test runs: Back button only. After failure: Back + Retry. */}
            {!testResult && (
              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep('setup')}
                  className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-md shadow-sm hover:bg-gray-50"
                >
                  Back
                </button>
              </div>
            )}
            {testResult && !testResult.success && (
              <div className="flex justify-between">
                <button
                  onClick={() => {
                    setTestResult(null);
                    setCurrentStep('setup');
                  }}
                  className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-md shadow-sm hover:bg-gray-50"
                >
                  Back to Setup
                </button>
                <button
                  onClick={() => {
                    setTestResult(null);
                    testConnection();
                  }}
                  disabled={loading}
                  className="px-6 py-3 bg-blue-600 text-white font-medium rounded-md shadow-sm hover:bg-blue-700 disabled:opacity-50"
                >
                  Retry Test
                </button>
              </div>
            )}
          </div>
        )}

        {/* Discovery Step */}
        {currentStep === 'discovery' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Smart Schema Discovery
            </h2>

            {loading ? (
              <div className="text-center py-8">
                <MagnifyingGlassIcon className="h-12 w-12 text-blue-600 mx-auto mb-4 animate-spin" />
                <h3 className="text-lg font-medium text-gray-900">Scanning your database...</h3>
                <p className="text-sm text-gray-600">This may take a moment</p>
              </div>
            ) : discoveryResult ? (
              <div className="space-y-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-green-900 mb-2 flex items-center">
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    Discovery complete
                  </h3>
                  {discoveryResult.access_scope === 'multi_database' ? (
                    <div className="text-sm text-green-700">
                      <p className="mb-2">
                        Found {discoveryResult.all_tables?.length || 0} tables across {discoveryResult.databases?.length || 0} databases
                      </p>
                      <div className="text-xs text-green-600">
                        {discoveryResult.databases?.map((db, index) => (
                          <span key={db.name}>
                            {db.name} ({db.total_tables} tables)
                            {index < discoveryResult.databases.length - 1 ? ', ' : ''}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-green-700">
                      Found {discoveryResult.tables?.length || 0} tables in {discoveryResult.database}.{discoveryResult.schema}
                    </p>
                  )}
                </div>

                {((discoveryResult.all_tables && discoveryResult.all_tables.length > 0) || (discoveryResult.tables && discoveryResult.tables.length > 0)) && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <div className="flex items-center mb-4">
                      <SparklesIcon className="h-6 w-6 text-blue-600 mr-2" />
                      <h3 className="text-lg font-medium text-gray-900">Smart Table Recommendations</h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      We've identified your most important tables based on size, naming patterns, and business relevance:
                    </p>
                    
                    <div className="space-y-2">
                      {(discoveryResult.all_tables || discoveryResult.tables || []).slice(0, 5).map((table, index) => (
                        <div key={table.full_name || table.name} className="flex items-center justify-between bg-white p-3 rounded border">
                          <div className="flex items-center">
                            <TableCellsIcon className="h-5 w-5 text-gray-400 mr-3" />
                            <div>
                              <div className="font-medium text-gray-900">{table.name}</div>
                              {table.full_name && (
                                <div className="text-xs text-gray-500">{table.database}.{table.schema}</div>
                              )}
                              <span className="text-sm text-gray-500">
                                ({table.row_count > 0 ? `${table.row_count.toLocaleString()} rows` : 'No data'}, {table.column_count} columns)
                              </span>
                            </div>
                          </div>
                          <CheckCircleIcon className="h-5 w-5 text-green-500" />
                        </div>
                      ))}
                    </div>

                    {((discoveryResult.all_tables || discoveryResult.tables || []).length > 5) && (
                      <p className="text-sm text-gray-500 mt-3">
                        + {(discoveryResult.all_tables || discoveryResult.tables || []).length - 5} more tables available
                      </p>
                    )}
                  </div>
                )}

                <div className="flex justify-between">
                  <button
                    onClick={() => setCurrentStep('test')}
                    className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-md shadow-sm hover:bg-gray-50"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleContinueToTableSelection}
                    className="px-6 py-3 bg-blue-600 text-white font-medium rounded-md shadow-sm hover:bg-blue-700"
                  >
                    Continue to Table Selection
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600">No discovery data available</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingConnectionPage;